import { useCallback, useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Images } from "lucide-react";
import axios from "axios";
import { API_URL } from "@/data/content";
import { SectionHead } from "@/components/Reveal";
import Parallax from "@/components/Parallax";
import WishShareModal, { renderWishCard } from "@/components/WishShareModal";

const MAX_CARDS = 24;

function GalleryCard({ w, onOpen }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (inView && !src) {
      renderWishCard(w).then(setSrc).catch(() => setSrc("failed"));
    }
  }, [inView, src, w]);
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.6 }}>
      <button
        onClick={() => onOpen(w)}
        data-testid={`gallery-card-${w.id}`}
        aria-label={`Open share options for the wish by ${w.name}`}
        className="group block w-full text-left"
      >
        <div className="overflow-hidden rounded-2xl border border-line/20 transition-all duration-500 group-hover:-translate-y-1 group-hover:border-gold/40 group-hover:shadow-[0_20px_60px_rgba(0,0,0,0.35)]">
          {src && src !== "failed" ? (
            <img src={src} alt={`Founder's Day wish card by ${w.name}`} className="w-full transition-transform duration-700 group-hover:scale-[1.03]" />
          ) : (
            <div className="flex aspect-square w-full items-center justify-center bg-panel/60">
              {src === "failed" ? <Images className="h-8 w-8 text-soft/50" /> : <div className="h-full w-full animate-pulse bg-panel" />}
            </div>
          )}
        </div>
        <div className="mt-3 flex items-center justify-between gap-3 px-1">
          <span className="text-xs font-semibold tracking-wide text-fg">{w.name}</span>
          <span className="rounded-full border border-gold/30 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-gold">
            {w.house && w.house !== "—" ? `${w.house} · ` : ""}
            {w.role}
          </span>
        </div>
      </button>
    </motion.div>
  );
}

export default function WishGallery() {
  const [wishes, setWishes] = useState([]);
  const [shareWish, setShareWish] = useState(null);

  const load = useCallback(() => {
    axios
      .get(`${API_URL}/wishes`)
      .then((r) => setWishes(r.data.slice(0, MAX_CARDS)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const onChanged = () => load();
    window.addEventListener("doon:wishes-changed", onChanged);
    return () => window.removeEventListener("doon:wishes-changed", onChanged);
  }, [load]);

  return (
    <section id="gallery" className="relative overflow-hidden py-24 lg:py-32">
      <Parallax speed={70}>
        <div className="pointer-events-none absolute right-0 top-1/4 h-[360px] w-[360px] rounded-full bg-gold/10 blur-[130px]" />
      </Parallax>
      <div className="mx-auto max-w-7xl px-6">
        <SectionHead
          eyebrow="The Wish Gallery"
          title="Cards from the Doon Family"
          sub={`The latest ${MAX_CARDS} wishes from the wall, each painted as a 91st Founder's Day card. Click any card to download it or share it straight to WhatsApp.`}
        />
        {wishes.length === 0 ? (
          <p className="text-center text-soft">The first wishes are still being written — be the one to start the wall.</p>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="wish-gallery-grid">
            {wishes.map((w) => (
              <GalleryCard key={w.id} w={w} onOpen={setShareWish} />
            ))}
          </div>
        )}
      </div>
      <WishShareModal wish={shareWish} onClose={() => setShareWish(null)} />
    </section>
  );
}
